import React, { useEffect, useState } from "react";
import { signInWithCustomToken } from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../firebase";

/**
 * TokenLoginPage - Handles authentication via custom Firebase token
 *
 * This page is used for development purposes to allow logging in as
 * any user using a custom token generated by the loginAsUser script.
 *
 * Usage:
 *   Navigate to /token-login?token=<custom_token>
 */
const TokenLoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setError("No token provided. Please use the loginAsUser script to generate a login URL.");
      return;
    }

    const authenticate = async () => {
      try {
        setStatus("loading");

        // Sign in with the custom token
        const userCredential = await signInWithCustomToken(auth, token);

        console.log("Successfully authenticated as:", userCredential.user.email || userCredential.user.uid);

        setStatus("success");

        // Redirect to dashboard after a brief delay
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 1000);
      } catch (err: any) {
        console.error("Token authentication failed:", err);
        setStatus("error");

        if (err.code === "auth/invalid-custom-token") {
          setError("Invalid or expired token. Please generate a new token using the loginAsUser script.");
        } else if (err.code === "auth/custom-token-mismatch") {
          setError("Token mismatch. The token was generated for a different Firebase project.");
        } else {
          setError(err.message || "Authentication failed. Please try again.");
        }
      }
    };

    authenticate();
  }, [searchParams, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
      <div className="w-full max-w-md px-4">
        <div className="p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50">
          <div className="text-center">
            {status === "loading" && (
              <>
                <div className="flex justify-center mb-4">
                  <svg className="w-12 h-12 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
                  Authenticating...
                </h2>
                <p className="mt-2 text-zinc-600 dark:text-zinc-400">
                  Please wait while we sign you in.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="flex justify-center mb-4">
                  <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
                  Authentication Successful
                </h2>
                <p className="mt-2 text-zinc-600 dark:text-zinc-400">
                  Redirecting to dashboard...
                </p>
              </>
            )}

            {status === "error" && (
              <>
                <div className="flex justify-center mb-4">
                  <svg className="w-12 h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
                  Authentication Failed
                </h2>
                <p className="mt-2 text-red-600 dark:text-red-400">
                  {error}
                </p>
                <button
                  onClick={() => navigate("/login")}
                  className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Go to Login
                </button>
              </>
            )}
          </div>
        </div>

        {/* Dev mode indicator */}
        <p className="mt-4 text-center text-xs text-zinc-500 dark:text-zinc-500">
          Development token login
        </p>
      </div>
    </div>
  );
};

export default TokenLoginPage;
